import type {
  EngineContext,
  FieldWarningRuleEntry,
  OpenActionSpec,
  PromptTemplate,
  ResolvedWarning,
  VariableSpec,
  WhenExpr,
} from "./types";

type TemplateMap = Record<string, PromptTemplate>;
type VariableMap = Record<string, VariableSpec>;

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeBudgetFrequency(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const raw = value.trim();
  if (!raw) return undefined;
  const upper = raw.toUpperCase();
  // Support API mode codes
  if (upper === "W") return "weekly";
  if (upper === "2W") return "2weekly";
  if (upper === "4W") return "4weekly";
  if (upper === "M") return "monthly";
  if (upper === "Q") return "quarterly";
  if (upper === "Y") return "yearly";
  if (upper === "SA" || upper === "6M") return "semiannual";

  // Support UI schema values (and minor formatting variants)
  const norm = raw
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/-/g, "");
  if (norm === "week" || norm === "weekly" || norm === "perweek") return "weekly";
  if (
    norm === "2weekly" ||
    norm === "twoweekly" ||
    norm === "fortnightly" ||
    norm === "per2weeks"
  )
    return "2weekly";
  if (norm === "4weekly" || norm === "fourweekly" || norm === "per4weeks")
    return "4weekly";
  if (norm === "month" || norm === "monthly" || norm === "permonth") return "monthly";
  if (norm === "quarter" || norm === "quarterly" || norm === "perquarter")
    return "quarterly";
  if (norm === "semiannual" || norm === "semiannually") return "semiannual";
  if (norm === "year" || norm === "yearly" || norm === "peryear") return "yearly";
  return undefined;
}

function toMonthlyBudgetAmount(amount: number, frequency?: string): number {
  // Match the budget screen's help text:
  // Weekly: (Amount × 52) ÷ 12
  // 2-Weekly: (Amount × 26) ÷ 12
  // 4-Weekly: (Amount × 13) ÷ 12
  // Monthly: Amount
  // Yearly: Amount ÷ 12
  switch (frequency) {
    case "weekly":
      return (amount * 52) / 12;
    case "2weekly":
      return (amount * 26) / 12;
    case "4weekly":
      return (amount * 13) / 12;
    case "quarterly":
      return amount / 3;
    case "semiannual":
      return amount / 6;
    case "yearly":
      return amount / 12;
    case "monthly":
    default:
      return amount;
  }
}

function parseCurrencyNumber(value: any): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "").replace(/£/g, "").trim();
    if (cleaned === "") return undefined;
    const n = Number.parseFloat(cleaned);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function roundToPennies(value: number): number {
  return Math.round(value * 100) / 100;
}

function monthlyComparable(
  amountOrObj: any,
  frequencyOrMode: any
): number | undefined {
  // Allow passing a question-group object ({ amount, frequency, ... }) directly
  let amountCandidate: any = amountOrObj;
  let freqCandidate: any = frequencyOrMode;

  if (freqCandidate === undefined && isRecordLike(amountOrObj)) {
    amountCandidate =
      (amountOrObj as any).amount ??
      (amountOrObj as any).value ??
      (amountOrObj as any).Amount ??
      amountOrObj;
    freqCandidate =
      (amountOrObj as any).frequency ??
      (amountOrObj as any).mode ??
      (amountOrObj as any).Mode;
  }

  const amount = parseCurrencyNumber(amountCandidate);
  if (amount === undefined) return undefined;
  const freq = normalizeBudgetFrequency(freqCandidate) ?? "monthly";
  return roundToPennies(toMonthlyBudgetAmount(amount, freq));
}

function formatGbp(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return "";
  const nf = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `£${nf.format(value)}`;
}

/**
 * Read a value from an object using a dotted path.
 *
 * Important: our form snapshots sometimes contain *flattened* dot keys
 * (e.g. `{ "publicTransport.amount": "10" }`) rather than nested objects.
 * This helper supports both:
 * - nested: `obj.publicTransport.amount`
 * - flattened: `obj["publicTransport.amount"]`
 */
function getValueAtPath(obj: any, path?: string): any {
  if (obj == null) return undefined;
  if (!path) return obj;

  // Fast path: exact key match for flattened objects
  if (isRecordLike(obj) && Object.prototype.hasOwnProperty.call(obj, path)) {
    return (obj as any)[path];
  }

  const parts = path.split(".").filter(Boolean);
  if (parts.length === 0) return undefined;

  let acc: any = obj;
  for (let i = 0; i < parts.length; i += 1) {
    if (acc == null) return undefined;

    // Support "rest-of-path" lookup at each level for flattened keys
    const remaining = parts.slice(i).join(".");
    if (isRecordLike(acc) && Object.prototype.hasOwnProperty.call(acc, remaining)) {
      return (acc as any)[remaining];
    }

    if (!isRecordLike(acc)) return undefined;
    const part = parts[i];
    if (!part) return undefined;
    acc = (acc as any)[part];
  }
  return acc;
}

function isBlankish(value: any): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  );
}

function resolveFromLiveValues(
  varSpec: VariableSpec | undefined,
  ctx: any
): any {
  if (!varSpec) return undefined;
  const live = ctx?.liveValues;
  if (!live || typeof live !== "object") return undefined;

  // Prefer canonical backend field name when available.
  if (varSpec.backendField && !isBlankish((live as any)[varSpec.backendField])) {
    return (live as any)[varSpec.backendField];
  }

  // Then try schema field path, which may be a nested form path.
  const schemaVal = getValueAtPath(live, varSpec.schemaFieldPath);
  if (!isBlankish(schemaVal)) return schemaVal;

  // Finally fall back to var id as a direct key in liveValues.
  if (varSpec.id && !isBlankish((live as any)[varSpec.id])) {
    return (live as any)[varSpec.id];
  }

  // Known legacy naming variant for accommodation type.
  if (varSpec.id === "housingTenure") {
    const v =
      (live as any).housingTenure ??
      (live as any).accommodationType ??
      (live as any).accomodationType;
    if (!isBlankish(v)) return v;
  }

  return undefined;
}

function parseComparableNumber(value: any): number | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "").replace(/£/g, "").trim();
    if (cleaned === "") return undefined;
    const n = Number.parseFloat(cleaned);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function countDigits(value: any): number {
  if (value === null || value === undefined) return 0;

  // Some callers may pass a whole question-group object. Prefer its amount-like field.
  let v: any = value;
  for (let i = 0; i < 3; i += 1) {
    if (!isRecordLike(v)) break;
    if (Object.prototype.hasOwnProperty.call(v, "amount")) {
      v = (v as any).amount;
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(v, "value")) {
      v = (v as any).value;
      continue;
    }
    break;
  }

  const rawFull = typeof v === "string" ? v : String(v ?? "");
  // Count digits BEFORE the first decimal point only (legacy parity).
  // Example: "12345.67" => 5 digits, not 7.
  const dotIdx = rawFull.indexOf(".");
  const raw = dotIdx >= 0 ? rawFull.slice(0, dotIdx) : rawFull;
  const matches = raw.match(/\d/g);
  return matches ? matches.length : 0;
}

function compareNumbers(
  op: "gt" | "gte" | "lt" | "lte",
  left: any,
  right: any
): boolean {
  const a = parseComparableNumber(left);
  const b = parseComparableNumber(right);
  if (a === undefined || b === undefined) return false;
  switch (op) {
    case "gt":
      return a > b;
    case "gte":
      return a >= b;
    case "lt":
      return a < b;
    case "lte":
      return a <= b;
    default:
      return false;
  }
}

function resolveVariableValue(
  varSpec: VariableSpec | undefined,
  ctx: any
): any {
  if (!varSpec) return undefined;

  // Computed helper: total dependents under 18 (under16 + 16-18).
  // Keep `haveChildrenUnder18` as the Y/N flag for conditions, but display the numeric sum.
  if (varSpec.id === "childrenUnder18Count") {
    const readInt = (v: unknown): number => {
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v === "string") {
        const parsed = Number.parseInt(v, 10);
        return Number.isFinite(parsed) ? parsed : 0;
      }
      return 0;
    };

    const under16 = readInt(
      (ctx?.profile as any)?.dependentChildrenUnder16 ??
        (ctx?.liveValues as any)?.dependentChildrenUnder16
    );
    const aged16Plus = readInt(
      (ctx?.profile as any)?.dependentChildrenAged16Plus ??
        (ctx?.liveValues as any)?.dependentChildrenAged16Plus ??
        // Legacy casing seen in some payloads
        (ctx?.profile as any)?.dependentchildrenaged16plus ??
        (ctx?.liveValues as any)?.dependentchildrenaged16plus
    );

    return under16 + aged16Plus;
  }

  if (varSpec.getValueFrom) {
    const source = varSpec.getValueFrom.source as keyof typeof ctx;
    const base = ctx?.[source];
    const v = getValueAtPath(base, varSpec.getValueFrom.path);
    if (!isBlankish(v)) return v;
    // Some screens provide profile answers only in live form values.
    const liveFallback = resolveFromLiveValues(varSpec, ctx);
    return !isBlankish(liveFallback) ? liveFallback : v;
  }

  const outerSourceMap: Record<string, keyof typeof ctx> = {
    income: "income",
    assets: "assets",
    "fixed-expenses": "expense",
    "flexible-expenses": "expense",
    debts: "debts",
    profile: "profile",
  };

  const mappedSource = outerSourceMap[varSpec.outer];
  if (mappedSource && ctx?.[mappedSource]) {
    const base = ctx[mappedSource];
    const schemaValue = getValueAtPath(base, varSpec.schemaFieldPath);
    if (!isBlankish(schemaValue)) return schemaValue;
    if (varSpec.backendField && base && typeof base === "object") {
      const backendValue = (base as any)[varSpec.backendField];
      if (!isBlankish(backendValue)) return backendValue;
    }

    // Tolerate common legacy spelling variant (accomodationType vs accommodationType)
    if (
      typeof varSpec.schemaFieldPath === "string" &&
      varSpec.schemaFieldPath.includes("accomodationType") &&
      base &&
      typeof base === "object" &&
      !isBlankish((base as any).accommodationType)
    ) {
      return (base as any).accommodationType;
    }
  }

  // Final fallback: many "prompt to remove" rules reference profile values,
  // but on some screens the latest answers live only in `liveValues`.
  const liveFallback = resolveFromLiveValues(varSpec, ctx);
  return !isBlankish(liveFallback) ? liveFallback : undefined;
}

function stringifyPlaceholderValue(value: any): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "number")
    return Number.isFinite(value) ? value.toString() : "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") {
    const trimmed = value.trim();
    const upper = trimmed.toUpperCase();
    // Many profile flags come through as "Y"/"N" strings. Render them user-friendly.
    if (upper === "Y") return "Yes";
    if (upper === "N") return "No";
    return trimmed;
  }
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function normalizeEnumString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  // Normalize common enum strings so warning rules tolerate harmless formatting diffs.
  // Examples:
  // - "Not working due to illness/disability" == "Not working due to illness/disability"
  // - "Tenant - Social" == "Tenant- social" == "Tenant -social"
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/\s*\/\s*/g, "/")
    .replace(/\s*-\s*/g, "-");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function buildTriggerKeyCandidates(field: unknown): string[] {
  const raw =
    typeof field === "string"
      ? field
      : field === null || field === undefined
        ? ""
        : String(field);
  const trimmed = raw.trim();
  if (!trimmed) return [];

  // Normalize "House Rent", "house-rent", "houseRent", "HOUSE_RENT" -> "HOUSE_RENT"
  let normalized = trimmed
    .replace(/[^a-zA-Z0-9]+/g, "_")
    // Critical: convert camelCase/PascalCase to snake_case before uppercasing.
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();

  // Some trigger keys are enumerated (e.g. OTHER_SALARY_WAGES_1). If a number suffix
  // is present without an underscore, insert one: OTHER_SALARY_WAGES1 -> OTHER_SALARY_WAGES_1
  normalized = normalized.replace(/([A-Z])(\d+)$/g, "$1_$2");

  const noUnderscores = normalized.replace(/_/g, "");

  const candidates = [trimmed, normalized, noUnderscores]
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  // De-dupe while keeping order
  return Array.from(new Set(candidates));
}

function applyTemplatePlaceholders(
  text: string,
  placeholders: string[] | undefined,
  data: Record<string, { label?: string; value?: any }>
): string {
  if (!text || !Array.isArray(placeholders) || placeholders.length === 0) {
    return text;
  }

  let result = text;
  placeholders.forEach((placeholder) => {
    const normalized = placeholder?.trim();
    if (!normalized) return;
    const token = `{${normalized}}`;
    if (!result.includes(token)) return;

    const [alias, prop] = normalized.split(".");
    if (!alias) return;
    const entry = data[alias];
    if (!entry) {
      // Safety: some legacy rules/templates include note placeholders even when
      // the corresponding note token isn't provided for the field. In that case,
      // fall back to the literal phrase so we don't render "Please review or .".
      if (alias === "note" && prop === "label") {
        result = result.split(token).join("add a note");
      } else {
        result = result.split(token).join("");
      }
      return;
    }

    let replacement = "";
    if (prop === "label") {
      replacement = entry.label ?? (alias === "note" ? "add a note" : "");
    } else if (prop === "value") {
      replacement = stringifyPlaceholderValue(entry.value);
    }

    result = result.split(token).join(replacement);
  });

  return result;
}

function evalWhen(expr: WhenExpr, ctx: any): boolean {
  if (typeof expr === "boolean") return expr;
  if (!expr || typeof expr !== "object") return false;
  const op = expr.op;
  const args = expr.args ?? [];

  const getVal = (v: any) => {
    if (v && typeof v === "object" && "var" in v) {
      const path = (v as any).var as string;
      return getValueAtPath(ctx, path);
    }
    return v;
  };

  switch (op) {
    case "eq": {
      const left = getVal(args[0]);
      const right = getVal(args[1]);
      // Our JSON warning rules frequently use `null` to mean "not answered",
      // but real profile/form payloads often represent the same state as `undefined`.
      // Treat null and undefined as equivalent ONLY for comparisons against null.
      if (right === null) return left == null; // null or undefined
      if (left === null) return right == null; // null or undefined
      return left === right;
    }
    case "ne": {
      const left = getVal(args[0]);
      const right = getVal(args[1]);
      if (right === null) return left != null; // not null/undefined
      if (left === null) return right != null; // not null/undefined
      return left !== right;
    }
    case "gt":
      return compareNumbers("gt", getVal(args[0]), getVal(args[1]));
    case "gte":
      return compareNumbers("gte", getVal(args[0]), getVal(args[1]));
    case "lt":
      return compareNumbers("lt", getVal(args[0]), getVal(args[1]));
    case "lte":
      return compareNumbers("lte", getVal(args[0]), getVal(args[1]));
    case "gtMonthly": {
      const a = monthlyComparable(getVal(args[0]), getVal(args[1]));
      const b = monthlyComparable(getVal(args[2]), getVal(args[3]));
      if (a === undefined || b === undefined) return false;
      return a > b;
    }
    case "in": {
      const left = getVal(args[0]);
      const arr = getVal(args[1]);
      if (!Array.isArray(arr)) return false;

      // Make "in" checks resilient for string enums coming from schemas/APIs.
      // Many legacy datasets differ only by casing/whitespace around "/" or "-".
      if (typeof left === "string") {
        const leftNorm = normalizeEnumString(left);
        if (!leftNorm) return false;
        return arr.some((item) => {
          if (typeof item === "string") {
            return normalizeEnumString(item) === leftNorm;
          }
          return item === left;
        });
      }

      return arr.includes(left);
    }
    case "and":
      return (args || []).every((a: any) => evalWhen(a, ctx));
    case "or":
      return (args || []).some((a: any) => evalWhen(a, ctx));
    case "not":
      return !evalWhen(args[0], ctx);
    case "triggerExceeded": {
      const triggers = getVal(args[0]);
      //console.log("triggers", triggers, args[0]);
      const field = getVal(args[1]);
      if (!triggers || !field) return false;
      // expected shape: triggers.exceeded[field] === true
      // Debug logging for BASIC_INCOME
      if (field === "BASIC_INCOME") {
      
      }
      if (isRecord(triggers.exceeded)) {
        const candidates = buildTriggerKeyCandidates(field);
        return candidates.some((key) => Boolean(triggers.exceeded[key]));
      }
      return false;
    }
    case "triggerExceededWithValue": {
      const triggers = getVal(args[0]);
      const field = getVal(args[1]);
      if (!triggers || !field) return false;
      // shape: exceeded[field] and maxByField[field] available
      const candidates = buildTriggerKeyCandidates(field);
      const exceeded = isRecord(triggers.exceeded)
        ? candidates.some((key) => Boolean(triggers.exceeded[key]))
        : false;
      const hasMax = isRecord(triggers.maxByField)
        ? candidates.some((key) => key in triggers.maxByField)
        : false;
      return exceeded && hasMax;
    }
    case "digitCountGte": {
      const left = getVal(args[0]);
      const thresholdRaw = getVal(args[1]);
      const threshold = Number(thresholdRaw);
      if (!Number.isFinite(threshold)) return false;
      return countDigits(left) >= threshold;
    }
    case "digitCountGt": {
      const left = getVal(args[0]);
      const thresholdRaw = getVal(args[1]);
      const threshold = Number(thresholdRaw);
      if (!Number.isFinite(threshold)) return false;
      return countDigits(left) > threshold;
    }
    default:
      return false;
  }
}

function resolveBindings(
  text: string,
  bindings: Record<string, any>,
  ctx: any
): string {
  let out = text;
  const getVal = (v: any) => {
    if (v && typeof v === "object" && "var" in v) {
      const path = (v as any).var as string;
      return path
        .split(".")
        .reduce((acc: any, key: string) => (acc ? acc[key] : undefined), ctx);
    }
    return v;
  };
  Object.entries(bindings).forEach(([key, val]) => {
    let resolved = getVal(val);
    if (key === "calc.max" && typeof resolved === "number") {
      resolved = formatGbp(resolved);
    }
    const token = `{${key}}`;
    out = out.split(token).join(String(resolved ?? ""));
  });
  return out;
}

function buildResolvedText(
  template: PromptTemplate,
  bindings: Record<string, any> | undefined,
  ctx: any
): string {
  let text = template.defaultText;
  if (bindings) {
    text = resolveBindings(text, bindings, ctx);
  }
  // Basic placeholder cleanup if any remain
  return text;
}

export function evaluateWarnings(
  context: EngineContext,
  templates: TemplateMap,
  variables: VariableMap,
  fieldRules: FieldWarningRuleEntry[],
  opts?: { max?: number }
): ResolvedWarning[] {
  const results: ResolvedWarning[] = [];
  const max = opts?.max ?? 5;
  const evalCtx = {
    profile: context.profile,
    income: context.income,
    expense: context.expense,
    assets: context.assets,
    debts: context.debts,
    triggers: context.triggers,
    section: context.section,
    schema: context.schema,
    liveValues: context.liveValues,
    calc: (() => {
      // Cross-field calcs used by templates (e.g. housing benefit vs rent).
      const lv = context.liveValues ?? {};

      const hbAmt =
        getValueAtPath(lv, "housingBenefitLocalAllowance.amount") ??
        getValueAtPath(lv, "housingBenefitLocalAllowance");
      const hbFreq =
        getValueAtPath(lv, "housingBenefitLocalAllowance.frequency") ??
        getValueAtPath(lv, "housingBenefitLocalAllowanceMode") ??
        (context.income as any)?.housingBenefitLocalAllowanceMode;
      const rentAmt =
        getValueAtPath(lv, "houseRent.amount") ?? getValueAtPath(lv, "houseRent");
      const rentFreq =
        getValueAtPath(lv, "houseRent.frequency") ??
        getValueAtPath(lv, "houseRentMode") ??
        (context.expense as any)?.houseRentMode;

      const hbMonthly = monthlyComparable(hbAmt, hbFreq);
      const rentMonthly = monthlyComparable(rentAmt, rentFreq);
      const diff =
        hbMonthly !== undefined && rentMonthly !== undefined
          ? roundToPennies(Math.max(0, hbMonthly - rentMonthly))
          : undefined;

      return {
        hb: formatGbp(hbMonthly),
        rent: formatGbp(rentMonthly),
        diff: formatGbp(diff),
      };
    })(),
  };

  for (const entry of fieldRules) {
    // Evaluate only for current outer section for perf
    if (entry.category && entry.category !== context.section.outer) continue;
    for (const rule of entry.rules) {
      const template = templates[rule.templateId];
      if (!template) continue;
      try {
        const debugField = "TVLICENCE";
        const shouldShow = evalWhen(rule.when, evalCtx);
        if (entry.fieldName === debugField) {
        }
        if (!shouldShow) continue;

        // If the template expects a note label (e.g. "... or {note.label}.") but the rule
        // didn't provide a note token, try to auto-attach the corresponding *_NOTES variable
        // for the current field. This keeps old rules working while still enabling
        // "add a note" to be a real clickable variable wherever the registry supports it.
        const rawRuleTokens = Array.isArray((rule as any).tokens)
          ? ([...(rule as any).tokens] as any[])
          : ([] as any[]);
        const templateNeedsNote =
          Array.isArray(template.placeholders) &&
          template.placeholders.includes("note.label");
        if (templateNeedsNote) {
          const hasNoteToken = rawRuleTokens.some(
            (t) => String((t as any)?.role).toLowerCase() === "note"
          );
          if (!hasNoteToken) {
            const candidateFromEntry = `${String(entry.fieldName)}_NOTES`;
            const fieldTok = rawRuleTokens.find(
              (t) => String((t as any)?.role).toLowerCase() === "field"
            );
            const candidateFromFieldToken = fieldTok?.varId
              ? `${String(fieldTok.varId)}_NOTES`
              : null;

            const resolvedNoteVarId =
              (candidateFromEntry && variables[candidateFromEntry]
                ? candidateFromEntry
                : null) ||
              (candidateFromFieldToken &&
              variables[String(candidateFromFieldToken)]
                ? String(candidateFromFieldToken)
                : null);

            if (resolvedNoteVarId) {
              rawRuleTokens.push({ role: "note", varId: resolvedNoteVarId });
            }
          }
        }

        const placeholderData: Record<string, { label?: string; value?: any }> =
          {};
        const tokenEntries = rawRuleTokens.map((t) => {
          const varSpec = variables[t.varId];
          const label = varSpec?.label ?? t.varId;
          // Primary value resolution from spec
          let value = resolveVariableValue(varSpec, evalCtx);
          // Fallback for housing tenure so warnings rendered from live values
          if (
            (value === undefined || value === null) &&
            varSpec?.id === "housingTenure"
          ) {
            value =
              evalCtx.liveValues?.housingTenure ??
              evalCtx.liveValues?.accommodationType ??
              evalCtx.liveValues?.accomodationType;
          }
          const alias = t.role || "field";
          if (!placeholderData[alias]) {
            placeholderData[alias] = { label, value };
          }
          return {
            definition: t,
            spec: varSpec,
            label,
          };
        });

        const tokens = tokenEntries.map((entry) => {
          const token: {
            varId: string;
            label: string;
            isValue: boolean;
            openAction?: OpenActionSpec;
          } = {
            varId: entry.definition.varId,
            label: entry.label,
            isValue: entry.spec?.isValueToken ?? false,
          };
          if (entry.spec?.openAction) {
            token.openAction = entry.spec.openAction;
          }
          return token;
        });

        let resolvedText = buildResolvedText(template, rule.bindings, evalCtx);
        resolvedText = applyTemplatePlaceholders(
          resolvedText,
          template.placeholders,
          placeholderData
        );

        const id = `${entry.fieldName}:${rule.templateId}`;
        const warning: ResolvedWarning = {
          id,
          templateId: rule.templateId,
          resolvedText,
          tokens,
          severity: rule.severity ?? "warning",
          level: rule.level ?? "field",
          fieldName: entry.fieldName,
        };
        if (tokens[0]?.openAction) {
          warning.deeplink = tokens[0].openAction;
        }

        // Debug logging for BASIC_INCOME
        // if (entry.fieldName === "BASIC_INCOME") {
        //   console.log(
        //     "[WARNING ENGINE DEBUG] Created warning for BASIC_INCOME:",
        //     {
        //       entryFieldName: entry.fieldName,
        //       ruleTemplateId: rule.templateId,
        //       warningFieldName: warning.fieldName,
        //       shouldShow,
        //       category: entry.category,
        //       sectionOuter: context.section.outer,
        //     }
        //   );
        // }

        results.push(warning);

        if (entry.fieldName === debugField) {

        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          "Warning rule evaluation error",
          entry.fieldName,
          rule.templateId,
          e
        );
      }
    }
  }

  // Dedupe by id keeping highest severity (error > warning > info)
  const rank = { error: 3, warning: 2, info: 1 } as const;
  const dedup = new Map<string, ResolvedWarning>();
  for (const w of results) {
    const existing = dedup.get(w.id);
    if (
      !existing ||
      rank[w.severity ?? "warning"] > rank[existing.severity ?? "warning"]
    ) {
      dedup.set(w.id, w);
    }
  }

  // Order: section-level first, then others (stable), then cap to max
  const ordered = Array.from(dedup.values()).sort((a, b) => {
    if ((a.level ?? "field") !== (b.level ?? "field")) {
      return a.level === "section" ? -1 : 1;
    }
    return 0;
  });

  return ordered.slice(0, max);
}
